// The Coach in the background, in the Android app (issue #217, phase 4).
//
// When Khronos leaves the screen, Android throttles the page's timers (to once
// a minute after a few minutes), so the Coach's countdown stalls and its cues
// stop. In the app, the native CoachService (mobile/android) takes over what
// the user hears and sees:
//
//   - The page sends it the cues still to come (buildCueTimeline), with the
//     wall-clock time each is due, whenever the Coach changes phase, pauses
//     or resumes. It plays them on time from sounds the page rendered with
//     the user's own sound set (renderCueSounds), while the app is in the
//     background. In the foreground the page plays its own, as on the web.
//   - Its notification shows the step and a countdown to its end, with
//     Pause, Resume and Skip.
//   - Notification-bar Pause and Skip come back here as actions, and the
//     Coach's clock catches up on the seconds it missed (useWorkoutCoach).
//
// It also posts the finish-by notices and alarm, and asks once to be exempt
// from battery optimisation so the phone doesn't stop it mid-workout.
//
// In a browser every function here is a no-op.

import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "./native";
import { toSeconds } from "../utils/time";
import {
  SOUND_PROFILES,
  DEFAULT_PROFILE,
  MIDWAY_BEEP_MIN_SECONDS,
  resolveSoundProfileKey,
} from "../config/soundProfiles";

const KhronosCoach = registerPlugin("KhronosCoach");

/** True inside the Android app. */
export const nativeCoachAvailable = () => isNativeApp();

// ─── What plays when ──────────────────────────────────────────────────────

// The cues a phase can open with (useWorkoutCoach's phase-cue effect).
export const PHASE_CUES = ["work", "rest", "set_rest", "warmup", "cooldown"];
// Everything the service may be asked to play.
export const NATIVE_CUES = [...PHASE_CUES, "countdown_tick", "halfway", "end"];

/** The cue a compiled queue item opens with. */
export function phaseCueFor(item) {
  const t = String(item?.node?.type || "").toLowerCase();
  const isRecovery = t.includes("rest") || t === "transition";
  if (isRecovery) {
    return item?.kind === "recovery" && item?.enclosingGroup?.isLastChild ? "set_rest" : "rest";
  }
  if (t.includes("warmup")) return "warmup";
  if (t.includes("cooldown")) return "cooldown";
  return "work";
}

/** What a step is called on the notification. */
export function stepTitle(node) {
  if (!node) return "Workout";
  const name = node.label || node.exercise_name;
  if (name) return String(name);
  const t = String(node.type || "").replace(/_/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Workout";
}

const stepAmount = (node) => {
  if (!node || node.value == null || !node.unit) return "";
  if (node.unit === "s" || node.unit === "m") return "";
  return `${node.value} ${node.unit}`;
};

// Stop looking ahead after this much: more than any one stretch of timed steps.
const HORIZON_MS = 4 * 60 * 60 * 1000;
const MAX_CUES = 4000;
// The Coach starts the next phase this long after the last second runs out.
const ADVANCE_MS = 100;

/**
 * The cues and notification steps still to come from `now`, mirroring how the
 * Coach would play them if the page kept running:
 *
 *   - a count-in ticks once a second, then the phase starts;
 *   - a timed phase ticks for its last three seconds (when ticks are on), at
 *     zero, and halfway through phases over MIDWAY_BEEP_MIN_SECONDS;
 *   - it then moves straight on to the next step with that step's cue, as
 *     long as the next one is timed too. A counted step waits for Done, so
 *     the timeline ends with its cue.
 *
 * Cues the Coach has already played (this phase's opening cue, the count-in
 * tick on screen now) aren't repeated.
 *
 * @returns {{ cues: {at:number,type:string}[], steps: {at:number,endsAt:number|null,title:string,text:string}[] }}
 */
export function buildCueTimeline({
  queue,
  currentIndex,
  engineStatus,
  countInRemaining = 0,
  timeLeftInPhase = 0,
  countUpMode = false,
  countdownEnabled = true,
  now,
}) {
  const cues = [];
  const steps = [];
  const item = queue?.[currentIndex];
  if (!item || !Array.isArray(queue)) return { cues, steps };

  const nextExecutable = (i) => {
    for (let j = i + 1; j < queue.length; j++) if (queue[j]?.executable !== false) return j;
    return -1;
  };
  const describe = (idx, at, endsAt) => {
    const it = queue[idx];
    const next = nextExecutable(idx);
    const amount = stepAmount(it.node);
    const parts = [];
    if (amount) parts.push(amount);
    if (next >= 0) parts.push(`Next: ${stepTitle(queue[next].node)}`);
    else parts.push("Last step");
    steps.push({ at, endsAt, title: stepTitle(it.node), text: parts.join(" · ") });
  };
  const push = (at, type) => {
    if (cues.length >= MAX_CUES) return;
    if (type === "countdown_tick" && !countdownEnabled) return;
    cues.push({ at: Math.round(at), type });
  };

  if (engineStatus === "countIn") {
    const r = Math.max(0, Math.round(countInRemaining));
    for (let k = 1; k < r; k++) push(now + k * 1000, "countdown_tick");
    // The step itself starts when the count-in ends, at its full length.
    const start = now + r * 1000;
    if (!item.isTimed) {
      describe(currentIndex, now, null);
      return { cues, steps };
    }
    return walk(currentIndex, start, toSeconds(item.node.value, item.node.unit), now);
  }

  if (engineStatus !== "running") {
    // Paused, waiting on Ready, finished: nothing plays until the page says.
    return { cues, steps };
  }
  if (countUpMode || !item.isTimed) {
    describe(currentIndex, now, null);
    return { cues, steps };
  }
  return walk(currentIndex, now, Math.max(0, Math.round(timeLeftInPhase)), now);

  // From step `idx`, `left` seconds of which remain at `base`: its ticks, then
  // every timed step after it.
  function walk(idx, base, left, shownFrom) {
    let i = idx;
    let t0 = base;
    let remaining = left;
    let shownAt = shownFrom;
    while (i >= 0 && t0 - now < HORIZON_MS && cues.length < MAX_CUES) {
      const it = queue[i];
      const total = toSeconds(it.node.value, it.node.unit);
      describe(i, shownAt, t0 + remaining * 1000);
      for (let s = 1; s <= remaining; s++) {
        const after = remaining - s;
        const at = t0 + s * 1000;
        if (after <= 3) push(at, "countdown_tick");
        else if (total > MIDWAY_BEEP_MIN_SECONDS && after === Math.floor(total / 2)) push(at, "halfway");
      }
      const next = nextExecutable(i);
      if (next < 0) break; // the end: its fanfare waits for Save
      const startAt = t0 + remaining * 1000 + ADVANCE_MS;
      const nextItem = queue[next];
      push(startAt, phaseCueFor(nextItem));
      if (!nextItem.isTimed) {
        describe(next, startAt, null);
        break;
      }
      i = next;
      t0 = startAt;
      shownAt = startAt;
      remaining = toSeconds(nextItem.node.value, nextItem.node.unit);
    }
    return { cues, steps };
  }
}

// ─── Talking to the service ───────────────────────────────────────────────

const quietly = (label) => (err) => console.warn(`[nativeCoach] ${label} failed`, err);

/**
 * Hand the service what's to come. `paused` keeps the notification (with
 * Resume) and plays nothing.
 */
export function syncNativeCoach({ workoutTitle, cues, steps, paused = false, volume = 0.7, muted = false }) {
  if (!nativeCoachAvailable()) return Promise.resolve();
  return Promise.resolve()
    .then(() =>
      KhronosCoach.sync({
        workoutTitle: workoutTitle || "Workout",
        cues: muted ? [] : cues,
        steps,
        paused,
        muted: !!muted,
        volume: Math.max(0, Math.min(1, Number(volume) || 0)),
      }),
    )
    .catch(quietly("sync"));
}

/** The workout ended, was saved or discarded. */
export function stopNativeCoach() {
  if (!nativeCoachAvailable()) return Promise.resolve();
  return Promise.resolve().then(() => KhronosCoach.stop()).catch(quietly("stop"));
}

/** A notice on the lock screen (finish-by), from the app rather than the browser. */
export function nativeNotify(title, body) {
  if (!nativeCoachAvailable()) return Promise.resolve(false);
  return Promise.resolve()
    .then(() => KhronosCoach.notify({ title, body }))
    .then(() => true)
    .catch((err) => {
      quietly("notify")(err);
      return false;
    });
}

/** The finish-by alarm, at wall-clock `at` (or null to clear it). */
export function setNativeAlarm(at, title, body) {
  if (!nativeCoachAvailable()) return Promise.resolve();
  return Promise.resolve()
    .then(() => KhronosCoach.setAlarm({ at: at || 0, title: title || "", body: body || "" }))
    .catch(quietly("setAlarm"));
}

/**
 * Buttons pressed on the notification, the lock screen or headphones:
 * handler({ action: "pause" | "resume" | "skip" | "mute" | "unmute", at }).
 */
export function onNativeCoachAction(handler) {
  if (!nativeCoachAvailable()) return () => {};
  let handle = null;
  let gone = false;
  Promise.resolve()
    .then(() => KhronosCoach.addListener("action", (e) => handler(e || {})))
    .then((h) => {
      if (gone) h?.remove?.();
      else handle = h;
    })
    .catch(quietly("addListener"));
  return () => {
    gone = true;
    handle?.remove?.();
  };
}

// ─── In the background? ───────────────────────────────────────────────────

// While the app is off screen the service plays the cues, so the page stays
// quiet (useSound checks this) and two copies never overlap.
let inBackground = false;
let watching = false;
const foregroundListeners = new Set();

export const nativeCuesOwnAudio = () => nativeCoachAvailable() && inBackground;

/**
 * Start following the app's foreground state. Idempotent. `onChange(active)`
 * hears each change until the returned function is called.
 */
export function watchAppForeground(onChange) {
  if (!nativeCoachAvailable()) return () => {};
  if (onChange) foregroundListeners.add(onChange);
  if (!watching) {
    watching = true;
    Promise.resolve()
      .then(() =>
        KhronosCoach.addListener("foreground", (e) => {
          const active = !!e?.active;
          inBackground = !active;
          foregroundListeners.forEach((fn) => fn(active));
        }),
      )
      .catch(quietly("foreground listener"));
  }
  return () => foregroundListeners.delete(onChange);
}

// ─── Battery optimisation ─────────────────────────────────────────────────

const BATTERY_ASKED_KEY = "khronos_battery_exemption_asked";

/**
 * Once, at the first workout in the app: Android's own "let Khronos run in
 * the background?" prompt. Without it some phones (Samsung, Xiaomi) stop the
 * service during a long call regardless of its notification.
 */
export async function askBatteryExemptionOnce() {
  if (!nativeCoachAvailable()) return;
  try {
    if (localStorage.getItem(BATTERY_ASKED_KEY)) return;
  } catch {
    return; // no storage: don't risk asking every workout
  }
  try {
    const { exempt } = (await KhronosCoach.batteryStatus()) || {};
    try {
      localStorage.setItem(BATTERY_ASKED_KEY, "1");
    } catch {
      /* asked once this session at least */
    }
    if (!exempt) await KhronosCoach.requestBatteryExemption();
  } catch (err) {
    quietly("battery")(err);
  }
}

// ─── The sounds ───────────────────────────────────────────────────────────

const SAMPLE_RATE = 22050;
let loadedProfile = null;
let loading = null;

/** 16-bit mono PCM WAV of `samples` (floats in −1..1), base64. */
export function encodeWavBase64(samples, sampleRate = SAMPLE_RATE) {
  const n = samples.length;
  const bytes = new Uint8Array(44 + n * 2);
  const view = new DataView(bytes.buffer);
  const text = (off, s) => {
    for (let i = 0; i < s.length; i++) bytes[off + i] = s.charCodeAt(i);
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + n * 2, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const v = Math.max(-1, Math.min(1, samples[i] || 0));
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** How long to render a cue for: its notes plus a tail for bells to ring out. */
export function cueLength(steps) {
  if (!Array.isArray(steps)) return 0;
  const body = steps.reduce((sum, s) => sum + (Number(s?.duration) || 0) + (Number(s?.rest) || 0), 0);
  return Math.min(6, body + 1.5);
}

/**
 * Render the user's sound set offline and give it to the service, once per
 * set. Uses the same scheduleCue as the live Coach, so it sounds the same.
 */
export function loadNativeCueSounds(profileKey) {
  if (!nativeCoachAvailable()) return Promise.resolve();
  const requested = resolveSoundProfileKey(profileKey);
  const key = SOUND_PROFILES[requested] ? requested : DEFAULT_PROFILE;
  if (loadedProfile === key) return Promise.resolve();
  if (loading?.key === key) return loading.promise;
  const promise = (async () => {
    const Tone = await import("tone");
    const { scheduleCue } = await import("../audio/scheduleCue");
    const profile = SOUND_PROFILES[key];
    const sounds = {};
    for (const type of NATIVE_CUES) {
      const steps = profile.sounds?.[type];
      const seconds = cueLength(steps);
      if (!seconds) continue;
      const buffer = await Tone.Offline(() => {
        const out = new Tone.Gain(1).connect(new Tone.Limiter(-1).toDestination());
        scheduleCue({ Tone, setKey: key, profile, type, at: 0, destination: out, cache: new Map() });
      }, seconds, 1, SAMPLE_RATE);
      sounds[type] = encodeWavBase64(buffer.getChannelData(0), SAMPLE_RATE);
    }
    await KhronosCoach.loadSounds({ profile: key, sounds });
    loadedProfile = key;
  })()
    .catch(quietly("loadSounds"))
    .finally(() => {
      if (loading?.promise === promise) loading = null;
    });
  loading = { key, promise };
  return promise;
}

/** Test hook. */
export const __resetNativeCoachForTests = () => {
  loadedProfile = null;
  loading = null;
  inBackground = false;
  watching = false;
  foregroundListeners.clear();
};
